import type {
  RouteLeg,
  RoutePassengerChanges,
  RoutePlanStop,
  RouteStayLeg,
} from '@/types/route-plan';
import type { TransportMode } from '@/types/transport';

/**
 * RouteTimelineCalculator — pure local re-flow of a route timeline.
 * No AI round trips: editing a travel duration or transport mode recomputes
 * all subsequent depart/arrive times and regenerates conflict warnings.
 *
 * Re-flow algorithm (deterministic):
 * 1. Walk legs forward from the edited leg.
 * 2. Travel leg: departTime = previous stay leg's departTime,
 *    arriveTime = departTime + durationMinutes.
 * 3. Stay leg: arriveTime = previous travel leg's arriveTime. Its stay
 *    duration (departTime − arriveTime before the edit) is preserved unless
 *    the stop has anchors: with fixedEnd, departTime = max(arriveTime,
 *    fixedEnd); with only fixedStart, departTime = max(arriveTime,
 *    fixedStart + original stay duration).
 * 4. Warnings: a stay leg whose stop has fixedStart gets
 *    "arrives HH:mm" when arriveTime > fixedStart; cleared otherwise.
 *
 * All functions are pure (no I/O) so they can gain unit tests when a test
 * framework lands in the repo.
 */
export class RouteTimelineCalculator {
  /**
   * Set a travel leg's duration and re-flow subsequent times + warnings.
   */
  static setTravelDuration(
    stops: RoutePlanStop[],
    legs: RouteLeg[],
    legId: string,
    durationMinutes: number,
  ): RouteLeg[] {
    const index = legs.findIndex((leg) => leg.id === legId);
    if (index === -1 || legs[index].kind !== 'travel') return legs;

    const next = legs.map((leg) =>
      leg.id === legId && leg.kind === 'travel'
        ? { ...leg, durationMinutes: Math.max(0, durationMinutes) }
        : leg,
    );
    return this.reflowFrom(stops, next, index);
  }

  /**
   * Set a travel leg's transport mode (times are unchanged; the duration
   * stepper is the user's tool for adjusting the estimate).
   */
  static setTravelMode(
    legs: RouteLeg[],
    legId: string,
    mode: TransportMode,
  ): RouteLeg[] {
    return legs.map((leg) =>
      leg.id === legId && leg.kind === 'travel' ? { ...leg, mode } : leg,
    );
  }

  /**
   * Replace a stay leg's passenger changes.
   */
  static setPassengerChanges(
    legs: RouteLeg[],
    legId: string,
    changes: RoutePassengerChanges,
  ): RouteLeg[] {
    return legs.map((leg) =>
      leg.id === legId && leg.kind === 'stay'
        ? { ...leg, passengerChanges: changes }
        : leg,
    );
  }

  /**
   * Remove a leg and re-flow. Deleting a travel leg snaps the next stay's
   * arrival to the previous stay's departure. Deleting a stay also removes
   * its preceding travel leg and drops the stop from the plan. Inline to-dos
   * on a removed travel leg reattach to the nearest surviving travel leg
   * (they remain in stops as unattached when none survives).
   */
  static removeLeg(
    stops: RoutePlanStop[],
    legs: RouteLeg[],
    legId: string,
  ): { stops: RoutePlanStop[]; legs: RouteLeg[] } {
    const index = legs.findIndex((leg) => leg.id === legId);
    if (index === -1) return { stops, legs };

    const target = legs[index];
    const orphanedTodoStopIds: string[] = [];
    let remainingStops = stops;
    let remainingLegs: RouteLeg[];
    let reflowIndex = index;

    if (target.kind === 'travel') {
      orphanedTodoStopIds.push(...(target.inlineTodoStopIds ?? []));
      remainingLegs = legs.filter((leg) => leg.id !== legId);
    } else {
      const previous = index > 0 ? legs[index - 1] : null;
      const removedIds = new Set([legId]);
      if (previous && previous.kind === 'travel') {
        removedIds.add(previous.id);
        orphanedTodoStopIds.push(...(previous.inlineTodoStopIds ?? []));
        reflowIndex = index - 1;
      }
      remainingLegs = legs.filter((leg) => !removedIds.has(leg.id));
      remainingStops = stops.filter((stop) => stop.id !== target.stopId);
    }

    const reattached = this.reattachInlineTodos(remainingLegs, orphanedTodoStopIds, reflowIndex);
    return {
      stops: remainingStops,
      legs: this.reflowFrom(remainingStops, reattached, reflowIndex),
    };
  }

  /**
   * Merge orphaned inline to-do stop ids into the travel leg nearest to
   * nearIndex (searching outward, preferring later legs).
   */
  private static reattachInlineTodos(
    legs: RouteLeg[],
    todoStopIds: string[],
    nearIndex: number,
  ): RouteLeg[] {
    if (todoStopIds.length === 0) return legs;

    let travelIndex = -1;
    for (let offset = 0; offset < legs.length; offset++) {
      const after = nearIndex + offset;
      const before = nearIndex - offset;
      if (after < legs.length && legs[after]?.kind === 'travel') {
        travelIndex = after;
        break;
      }
      if (before >= 0 && legs[before]?.kind === 'travel') {
        travelIndex = before;
        break;
      }
    }
    if (travelIndex === -1) return legs;

    return legs.map((leg, legIndex) =>
      legIndex === travelIndex && leg.kind === 'travel'
        ? { ...leg, inlineTodoStopIds: [...(leg.inlineTodoStopIds ?? []), ...todoStopIds] }
        : leg,
    );
  }

  /**
   * Re-flow depart/arrive times from the leg at fromIndex onwards and
   * regenerate conflict warnings on every stay leg.
   */
  static reflowFrom(
    stops: RoutePlanStop[],
    legs: RouteLeg[],
    fromIndex: number,
  ): RouteLeg[] {
    const stopsById = new Map(stops.map((stop) => [stop.id, stop]));
    const result: RouteLeg[] = legs.map((leg) => ({ ...leg }));

    for (let i = Math.max(0, fromIndex); i < result.length; i++) {
      const leg = result[i];
      const previous = i > 0 ? result[i - 1] : null;

      if (leg.kind === 'travel') {
        if (previous && previous.kind === 'stay') {
          leg.departTime = previous.departTime;
        }
        leg.arriveTime = this.addMinutes(leg.departTime, leg.durationMinutes);
      } else {
        const stayDuration = this.stayDurationMinutes(leg);
        if (previous && previous.kind === 'travel') {
          leg.arriveTime = previous.arriveTime;
        } else if (previous && previous.kind === 'stay') {
          // Adjacent stays happen when a travel leg was deleted: arrival
          // snaps to the previous stay's departure.
          leg.arriveTime = previous.departTime;
        }
        leg.departTime = this.stayDepartTime(leg, stopsById.get(leg.stopId), stayDuration);
      }
    }

    return this.withWarnings(stops, result);
  }

  /**
   * Regenerate arrival-conflict warnings on all stay legs.
   */
  static withWarnings(stops: RoutePlanStop[], legs: RouteLeg[]): RouteLeg[] {
    const stopsById = new Map(stops.map((stop) => [stop.id, stop]));

    return legs.map((leg) => {
      if (leg.kind !== 'stay') return leg;
      const stop = stopsById.get(leg.stopId);
      const { warning: _previousWarning, ...rest } = leg;

      if (
        stop?.fixedStart &&
        this.toMinutes(leg.arriveTime) > this.toMinutes(stop.fixedStart)
      ) {
        return { ...rest, warning: `arrives ${leg.arriveTime}` };
      }
      return rest;
    });
  }

  /**
   * Progress helper: completed count out of total actionable legs.
   */
  static progress(legs: RouteLeg[], completedLegIds: string[]): { completed: number; total: number } {
    const completedSet = new Set(completedLegIds);
    return {
      completed: legs.filter((leg) => completedSet.has(leg.id)).length,
      total: legs.length,
    };
  }

  private static stayDurationMinutes(leg: RouteStayLeg): number {
    return Math.max(0, this.toMinutes(leg.departTime) - this.toMinutes(leg.arriveTime));
  }

  private static stayDepartTime(
    leg: RouteStayLeg,
    stop: RoutePlanStop | undefined,
    originalStayDuration: number,
  ): string {
    const arriveMinutes = this.toMinutes(leg.arriveTime);

    if (stop?.fixedEnd) {
      return this.toHHmm(Math.max(arriveMinutes, this.toMinutes(stop.fixedEnd)));
    }
    if (stop?.fixedStart) {
      const anchoredDepart = this.toMinutes(stop.fixedStart) + originalStayDuration;
      return this.toHHmm(Math.max(arriveMinutes, anchoredDepart));
    }
    return this.toHHmm(arriveMinutes + originalStayDuration);
  }

  /** Parse HH:mm to minutes since midnight. */
  static toMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map((segment) => parseInt(segment, 10));
    return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
  }

  /** Format minutes since midnight as HH:mm, clamped to 23:59. */
  static toHHmm(totalMinutes: number): string {
    const clamped = Math.min(Math.max(0, totalMinutes), 23 * 60 + 59);
    const hours = Math.floor(clamped / 60);
    const minutes = clamped % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  /** Add minutes to an HH:mm time. */
  static addMinutes(time: string, minutes: number): string {
    return this.toHHmm(this.toMinutes(time) + minutes);
  }
}
